import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, MapPin, Clock, Lock, ChevronDown, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import useSeo from "../hooks/useSeo";
import usePriorityStates from "../hooks/usePriorityStates";
import StateSelect from "../components/StateSelect";
import { fieldCls } from "../components/QuickMatchForm";
import { submitApplication, isValidEmail, isValidPhone } from "../lib/apply";

const PERKS = ["100% remote — work from home", "Direct hire with a vetted US employer", "Recruiter reply within 2 business days", "Free for job seekers, always"];

function JobDetails({ job }) {
  return (
    <div className="rounded-2xl bg-white p-6 md:p-8 ring-1 ring-[#0F2A1F]/10" data-testid="apply-job-details">
      <h2 className="text-2xl font-bold text-[#0F2A1F]">About the role</h2>
      <p className="mt-3 text-[#0F2A1F]/75 leading-relaxed">{job.description}</p>
      {(job.software?.length || 0) > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {job.software.map((s) => <span key={s} className="rounded-lg bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/10 px-2.5 py-1 text-xs font-medium text-[#0F2A1F]/70">{s}</span>)}
        </div>
      )}
      <h3 className="mt-8 text-xl font-bold text-[#0F2A1F]">What you get</h3>
      <ul className="mt-3 space-y-2.5">
        {PERKS.map((p) => <li key={p} className="flex items-start gap-2.5 text-[15px] text-[#0F2A1F]/80"><CheckCircle2 className="h-5 w-5 shrink-0 text-[#10B981]" /> {p}</li>)}
      </ul>
    </div>
  );
}

export default function Apply() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const states = usePriorityStates();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", note: "" });
  const [state, setState] = useState("");
  const [cv, setCv] = useState(null);
  const [extras, setExtras] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/jobs/${jobId}`).then((r) => { if (!r.ok) throw new Error("404"); return r.json(); })
      .then((d) => { setJob(d); if (d.state) setState(d.state); setLoading(false); }).catch(() => setLoading(false));
  }, [jobId]);

  useSeo(job ? {
    title: `${job.title} · ${job.location} · Direct Hire Team`,
    description: `${job.title} — ${job.salary_range}. ${job.description.slice(0, 155)}`,
    url: `https://directhireteam.com/apply/${job.id}`,
    type: "article",
  } : { title: "Apply · Direct Hire Team", description: "Apply for a remote role with Direct Hire Team.", url: `https://directhireteam.com/apply/${jobId}` });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your name.");
    if (!isValidEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!isValidPhone(form.phone)) return toast.error("Please enter a valid phone number.");
    if (!state) return toast.error("Please select your state.");
    setBusy(true);
    try {
      const res = await submitApplication({ ...form, state, target_role: job.title, job_id: jobId }, cv);
      nav("/thank-you", { state: { role: job.title, name: form.full_name, tier: res.priority_tier, appId: res.id } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed. Please try again.");
      setBusy(false);
    }
  };

  if (loading) return <div className="pt-32 min-h-screen bg-[#FAF7EE] flex justify-center text-[#0F2A1F]/60"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!job) return (
    <div className="pt-32 min-h-screen bg-[#FAF7EE] px-6">
      <div className="max-w-md mx-auto text-center">
        <h1 className="text-4xl font-bold text-[#0F2A1F]">This role was just filled</h1>
        <p className="mt-3 text-[#0F2A1F]/65">Good news — there are hundreds more like it.</p>
        <button onClick={() => nav("/jobs")} className="mt-6 h-12 rounded-xl bg-[#0F2A1F] text-white px-6 text-sm font-semibold hover:bg-[#163b2c]" data-testid="back-to-jobs">See open roles</button>
      </div>
    </div>
  );

  const isHigh = states.high_priority.some((s) => s.code === state);
  const isPriority = !isHigh && states.priority.some((s) => s.code === state);

  return (
    <div className="pt-24 md:pt-28 pb-20 min-h-screen bg-[#FAF7EE]" data-testid="apply-page">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-[#0F2A1F]/70 hover:text-[#0F2A1F]" data-testid="apply-back"><ArrowLeft className="h-4 w-4" /> Back</button>
        <header className="mt-3" data-testid="apply-job-card">
          <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold">
            <span className="rounded-full bg-[#10B981]/15 text-[#0d7a55] px-2.5 py-1">Remote</span>
            <span className="rounded-full bg-[#D4AF37]/20 text-[#7a5f12] px-2.5 py-1">Hiring now</span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-[#0F2A1F] leading-tight">{job.title}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[15px] text-[#0F2A1F]/70">
            <span className="font-semibold text-[#0F2A1F]">{job.salary_range}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {job.experience}</span>
          </div>
        </header>

        <div className="mt-6 grid lg:grid-cols-12 gap-6 items-start">
          <form onSubmit={submit} noValidate className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:sticky lg:top-24 rounded-2xl bg-white p-5 sm:p-7 ring-1 ring-[#0F2A1F]/10 shadow-[0_24px_60px_-35px_rgba(15,42,31,0.5)] space-y-3" data-testid="apply-form">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0F2A1F]">Quick apply</h2>
              <span className="rounded-full bg-[#10B981]/15 text-[#0d7a55] text-[11px] font-bold px-2.5 py-1">60 SEC</span>
            </div>
            <p className="text-sm text-[#0F2A1F]/60 !mt-1">No account required. CV optional.</p>
            <input className={fieldCls} placeholder="Full name" autoComplete="name" value={form.full_name} onChange={set("full_name")} data-testid="apply-name" />
            <input className={fieldCls} type="email" inputMode="email" placeholder="Email address" autoComplete="email" value={form.email} onChange={set("email")} data-testid="apply-email" />
            <input className={fieldCls} type="tel" inputMode="tel" placeholder="Phone number" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="apply-phone" />
            <StateSelect value={state} onChange={setState} placeholder="Which state do you live in?" className={fieldCls} testId="apply-state-select" />
            {isHigh && <p className="text-xs text-[#0d7a55] font-semibold" data-testid="state-priority-notice">★ Texas & Oklahoma applicants get top-priority matching.</p>}
            {isPriority && <p className="text-xs text-[#0F2A1F]/70" data-testid="state-priority-notice">We're actively hiring in your state right now.</p>}

            <button type="button" onClick={() => setExtras((v) => !v)} className="w-full flex items-center justify-between rounded-lg px-1 py-2 text-sm font-semibold text-[#0F2A1F]" data-testid="apply-extras-toggle">
              <span>Add CV or a note <span className="font-normal text-[#0F2A1F]/50">(optional — helps you stand out)</span></span>
              <ChevronDown className={`h-4 w-4 transition-transform ${extras ? "rotate-180" : ""}`} />
            </button>
            <div className={extras ? "space-y-3" : "hidden"}>
              <input ref={fileRef} type="file" accept="application/pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="apply-cv-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full rounded-lg bg-[#FAF7EE] ring-1 ring-dashed ring-[#0F2A1F]/20 hover:ring-[#0F2A1F]/40 p-4 flex items-center gap-3 text-left" data-testid="apply-cv-btn">
                <Upload className="h-5 w-5 text-[#0F2A1F]/60" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0F2A1F] text-sm truncate">{cv ? cv.name : "Upload your CV"}</div>
                  <div className="text-xs text-[#0F2A1F]/55">{cv ? `${Math.round(cv.size / 1024)} KB` : "PDF or DOCX · max 8MB"}</div>
                </div>
              </button>
              <textarea rows={3} className={`${fieldCls} h-auto py-3`} value={form.note} onChange={set("note")} placeholder="Availability, salary expectations, a win you're proud of…" data-testid="apply-note" />
            </div>

            <button type="submit" disabled={busy} className="w-full h-14 rounded-xl bg-[#D4AF37] text-[#0F2A1F] text-base font-bold inline-flex items-center justify-center gap-2 hover:bg-[#e2bf48] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-70" data-testid="apply-submit-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send my application <ArrowRight className="h-5 w-5" /></>}
            </button>
            <p className="flex items-start gap-1.5 text-xs text-[#0F2A1F]/55"><Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> We only share your profile with employers in our network for this and similar roles. Never sold.</p>
          </form>
          <div className="lg:col-span-7 lg:row-start-1"><JobDetails job={job} /></div>
        </div>
      </div>
    </div>
  );
}
